import React, { useEffect, useState } from "react";
import { Avatar, Card, Tabs, Typography, Descriptions, Tag, Skeleton } from "antd";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import axios from "axios";
import { isAuthenticated } from "../../../commons/Auth/auth";
import UpdateProfile from "./UpdateProfile";
import UpdatePassword from "./UpdatePassword";
import UpdateBankDetails from "./UpdateBankDetails";
import Preferences from "./Preferences";
import CompanyLogo from "./CompanyLogo";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { useTranslation } from "react-i18next";
import { UserOutlined } from "@ant-design/icons";
import { EmpShifts } from "../../../commons/Employees/EmpShifts/EmpShifts";
import ContractList from "../contracts/ContractList";
import DeleteAccount from "../../../commons/Auth/DeleteAccount";
import TrainingRecords, { useTrainingRecords } from "../apprenticeship/TrainingRecords";

const { Title, Text } = Typography;

// Employee.picture is a URL string, the other accounts store the upload object.
export const pictureUrl = picture => (typeof picture === "string" ? picture : picture?.url);

const joinParts = (parts, separator = " ") => parts.filter(Boolean).join(separator);

const Profile = () => {
  const { t } = useTranslation("dashboard");
  const [user, setUser] = useState(null);
  const isEmployee = isAuthenticated()?.role === 1;
  const isOwner = isAuthenticated()?.role === 2;
  const isMobile = useIsMobile();
  // Apprentices and their trainers reach the Berichtsheft from here
  const trainingRecords = useTrainingRecords(isEmployee);

  const getUser = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/users/me`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      setUser(res.data);
    } catch (err) {
      generalErrorAlert(err);
      setUser({});
    }
  };

  useEffect(() => {
    getUser();
  }, []);

  if (!user) {
    return (
      <Card>
        <Skeleton avatar active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  const notProvided = <Text type="secondary">{t("Not provided")}</Text>;
  const show = value => value || notProvided;

  const address = isEmployee
    ? [joinParts([user.street, user.streetNumber]), joinParts([user.zipCode, user.city])]
    : [user.address, joinParts([user.zipCode, user.city])];
  const fullAddress = joinParts([...address, user.state, user.country], ", ");

  const employer =
    user.employer?.companyName || joinParts([user.employer?.firstName, user.employer?.lastName]);
  const company = isEmployee ? employer : user.companyName;

  const details = [
    { key: "name", label: t("Name"), children: show(joinParts([user.firstName, user.lastName])) },
    { key: "email", label: t("Email"), children: show(user.email) },
    user.username && { key: "username", label: t("Username"), children: user.username },
    { key: "phone", label: t("Phone"), children: show(user.phone) },
    { key: "dob", label: t("Date of Birth"), children: show(user.birthDate || user.dob) },
    isEmployee
      ? { key: "role", label: t("Role"), children: show(user.roleId?.title) }
      : { key: "taxNumber", label: t("Tax Number"), children: show(user.taxNumber) },
    {
      key: "company",
      label: isEmployee ? t("Employer") : t("Company Name"),
      children: show(company)
    },
    !isEmployee && { key: "iban", label: t("IBAN"), children: show(user.bankDetails?.iban) },
    { key: "address", label: t("Address"), children: show(fullAddress), span: 2 },
    { key: "description", label: t("Description"), children: show(user.description), span: 2 }
  ].filter(Boolean);

  const tabs = [
    {
      key: "about",
      label: t("About"),
      children: (
        <>
          {isOwner && <CompanyLogo logo={user.logo} updateParent={getUser} />}
          <Card title={t("Profile")} extra={<UpdateProfile data={user} updateParent={getUser} />}>
            <Descriptions bordered column={{ xs: 1, md: 2 }} items={details} />
            {isEmployee && (
              <Text type="secondary" className="d-block mt-3">
                {t("Your name, email and date of birth are managed by your employer.")}
              </Text>
            )}
          </Card>
        </>
      )
    },
    !isEmployee && {
      key: "bank",
      label: t("Bank Details"),
      children: <UpdateBankDetails data={user.bankDetails} updateParent={getUser} />
    },
    {
      key: "preferences",
      label: t("Preferences"),
      children: <Preferences data={user} updateParent={getUser} />
    },
    {
      key: "password",
      label: t("Password"),
      children: (
        <>
          <UpdatePassword />
          <DeleteAccount />
        </>
      )
    },
    isEmployee && {
      key: "shifts",
      label: t("My Shifts"),
      children: <EmpShifts employeeObject={{ _id: user._id }} selfService />
    },
    isEmployee && {
      key: "contracts",
      label: t("My Contracts"),
      children: <ContractList employeeId={user._id} selfService />
    },
    isEmployee &&
      trainingRecords.length > 0 && {
        key: "training",
        label: t("Training record"),
        children: <TrainingRecords records={trainingRecords} />
      }
  ].filter(Boolean);

  return (
    <>
      <Card className="mb-3">
        <div className="d-flex align-items-center gap-3">
          <Avatar
            size={isMobile ? 72 : 100}
            icon={<UserOutlined />}
            src={pictureUrl(user.picture)}
            style={{ flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <Title level={4} className="mb-1" style={{ overflowWrap: "anywhere" }}>
              {joinParts([user.firstName, user.lastName])}
            </Title>
            <Text type="secondary" style={{ overflowWrap: "anywhere" }}>
              {user.email}
            </Text>
            <div className="mt-2 d-flex flex-wrap gap-1">
              <Tag color="blue">
                {isEmployee ? user.roleId?.title || t("Employee") : t("Service Provider")}
              </Tag>
              {company && <Tag>{company}</Tag>}
            </div>
          </div>
        </div>
      </Card>
      <Tabs defaultActiveKey="about" items={tabs} />
    </>
  );
};

export default Profile;
